import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { LayoutGroup, MotionConfig } from "motion/react"
import { StrictMode, useEffect } from "react"
import { createRoot } from "react-dom/client"
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router"
import { api } from "@/api/cliente"
import { AppShell } from "@/componentes/layout/AppShell"
import { Escenario } from "@/componentes/layout/Escenario"
import { PaginaDocumentoTH } from "@/componentes/talento/PaginaDocumentoTH"
import { PilaAvisos } from "@/componentes/ui/PilaAvisos"
import { crearCacheMutaciones } from "@/lib/avisosMutacion"
import { engancharBitacora } from "@/lib/bitacora"
import "@/estilos/globales.css"
import { esCliente } from "@/lib/autorizacion"
import { type UsuarioSesion, usarSesion } from "@/lib/sesion"
import { ES_PORTAL_TRANSPORTE, NOMBRE_PORTAL_TRANSPORTE } from "@/lib/version"
import { Auditoria } from "@/paginas/admin/Auditoria"
import { Empresa } from "@/paginas/admin/Empresa"
import { Usuarios } from "@/paginas/admin/Usuarios"
import { DisponibilidadPublica } from "@/paginas/DisponibilidadPublica"
import { ExpedientesPublica } from "@/paginas/ExpedientesPublica"
import { GastosPublica } from "@/paginas/GastosPublica"
import { IngresoPublica } from "@/paginas/IngresoPublica"
import { Avisos } from "@/paginas/interno/Avisos"
import { Administracion, RutaNominaVieja } from "@/paginas/interno/administracion/Administracion"
import { Buzon } from "@/paginas/interno/Buzon"
import { Clientes } from "@/paginas/interno/Clientes"
import { CotizacionDetalle } from "@/paginas/interno/CotizacionDetalle"
import { Cotizaciones } from "@/paginas/interno/Cotizaciones"
import { Credenciales } from "@/paginas/interno/Credenciales"
import { Cumplimiento } from "@/paginas/interno/Cumplimiento"
import { RutaComercialVieja } from "@/paginas/interno/comercial/RutasViejas"
import { DocumentoSGI } from "@/paginas/interno/DocumentoSGI"
import { Drive } from "@/paginas/interno/Drive"
import { ExpedienteDetalle } from "@/paginas/interno/ExpedienteDetalle"
import { Expedientes } from "@/paginas/interno/Expedientes"
import { Fletes } from "@/paginas/interno/Fletes"
import { Guia } from "@/paginas/interno/Guia"
import { Inicio } from "@/paginas/interno/Inicio"
import { LibroCompras } from "@/paginas/interno/LibroCompras"
import { LibroCuentas } from "@/paginas/interno/LibroCuentas"
import { ManualIdentidad } from "@/paginas/interno/ManualIdentidad"
import { MapaSistema } from "@/paginas/interno/MapaSistema"
import { MiFicha } from "@/paginas/interno/MiFicha"
import { Pendientes } from "@/paginas/interno/Pendientes"
import { PlanCuentas } from "@/paginas/interno/PlanCuentas"
import { Plantillas } from "@/paginas/interno/Plantillas"
import { Proveedores } from "@/paginas/interno/Proveedores"
import {
  DocumentoViejo,
  Planificacion,
  RutaVieja,
} from "@/paginas/interno/planificacion/Planificacion"
import { Reportes } from "@/paginas/interno/Reportes"
import { ResumenMacro } from "@/paginas/interno/ResumenMacro"
import { RetencionesProvisionales } from "@/paginas/interno/RetencionesProvisionales"
import { Rotulacion } from "@/paginas/interno/Rotulacion"
import { SistemaDeDiseno } from "@/paginas/interno/SistemaDeDiseno"
import {
  CadenaSuministros,
  RutaInventarioVieja,
} from "@/paginas/interno/suministros/CadenaSuministros"
import { Tareas } from "@/paginas/interno/Tareas"
import { TiposProveedor } from "@/paginas/interno/TiposProveedor"
import { Evaluacion } from "@/paginas/interno/talento/Evaluacion"
import { ExpedienteLaboral } from "@/paginas/interno/talento/ExpedienteLaboral"
import { MisEvaluaciones } from "@/paginas/interno/talento/MisEvaluaciones"
import { MisSolicitudes } from "@/paginas/interno/talento/MisSolicitudes"
import { TalentoHumano } from "@/paginas/interno/talento/TalentoHumano"
import { Tesoreria } from "@/paginas/interno/tesoreria/Tesoreria"
import { Login } from "@/paginas/Login"
import { MacroPublica } from "@/paginas/MacroPublica"
import { OrdenPublica } from "@/paginas/OrdenPublica"
import { Perfil } from "@/paginas/Perfil"
import { BuzonPortal } from "@/paginas/portal/Buzon"
import { DespachoDetalle } from "@/paginas/portal/DespachoDetalle"
import { MiEstadoCuenta } from "@/paginas/portal/MiEstadoCuenta"
import { MisDespachos } from "@/paginas/portal/MisDespachos"
import { ReportePublico } from "@/paginas/ReportePublico"
import { SemanaPublica } from "@/paginas/SemanaPublica"
import { TareaPublica } from "@/paginas/TareaPublica"
import { PaginaCargas, PaginaCatalogo, PaginaRecibos } from "@/paginas/transporte/PortalTransporte"

const clienteQuery = new QueryClient({
  // Los avisos de éxito —y los de error donde la pantalla no los pinta— se
  // declaran en la mutación con `meta` y los dispara esta caché (ver
  // `lib/avisosMutacion.ts`).
  mutationCache: crearCacheMutaciones(),
  defaultOptions: {
    queries: {
      // El caché de servidor vive aquí, no en Zustand. 30 s en catálogos, 0 en
      // lo que cambia; el sondeo se declara por consulta, no globalmente.
      staleTime: 30_000,
      retry: (intentos, error) => {
        // Reintentar un 401 o un 403 no arregla nada y retrasa el redirect.
        const estado = (error as { estado?: number })?.estado
        if (estado === 401 || estado === 403 || estado === 404) return false
        return intentos < 2
      },
    },
  },
})

/** Pide la sesión una vez al arrancar. Mientras tanto nada decide nada. */
function ArranqueSesion({ children }: { children: React.ReactNode }) {
  const { setUsuario, setCargando } = usarSesion()

  useEffect(() => {
    let vivo = true
    setCargando(true)
    api
      .get<UsuarioSesion>("/api/v1/auth/yo")
      .then((u) => vivo && setUsuario(u))
      .catch(() => vivo && setUsuario(null))
    return () => {
      vivo = false
    }
  }, [setUsuario, setCargando])

  return <>{children}</>
}

/**
 * Guarda de ruta. **Cosmética**: el servidor decide en cada request.
 *
 * Guarda la ruta de origen para devolver al usuario donde estaba tras entrar —
 * mandarlo siempre al inicio le hace repetir la navegación cada vez que expira
 * la sesión, y expira cada doce horas.
 */
function Protegida({ children }: { children: React.ReactNode }) {
  const { usuario, cargando } = usarSesion()
  const ubicacion = useLocation()

  if (cargando) {
    return (
      <div className="flex h-dvh items-center justify-center" style={{ color: "var(--fg-muted)" }}>
        Cargando…
      </div>
    )
  }
  if (!usuario) {
    return <Navigate to="/login" replace state={{ desde: ubicacion.pathname }} />
  }
  return <>{children}</>
}

function Entrada() {
  const usuario = usarSesion((e) => e.usuario)
  // Un usuario CLIENTE aterriza en su portal, nunca en el panel interno: el panel
  // cuenta trabajo de la agencia y no tiene nada que decirle a un importador.
  return esCliente(usuario) ? <Navigate to="/portal" replace /> : <Inicio />
}

function Marcador({ titulo }: { titulo: string }) {
  return (
    <div>
      <h1 className="text-xl font-semibold">{titulo}</h1>
      <p className="mt-2 text-sm" style={{ color: "var(--fg-muted)" }}>
        Esta sección llega en un paso posterior del orden de construcción.
      </p>
    </div>
  )
}

/**
 * Las rutas, con la transición de entrada al sistema.
 *
 * ⚠ **`LayoutGroup`, y NO `AnimatePresence` con `location` y `key`.** Ese fue el
 * primer intento y rompió la aplicación: al pasarle a `<Routes>` una `location`
 * congelada dentro de un contenedor con `key`, la rama saliente seguía recibiendo la
 * ubicación NUEVA y la resolvía contra el comodín — se entraba al sistema y salía
 * «Ruta no encontrada». Un enrutador es lo último que se debe tocar para conseguir
 * una animación.
 *
 * `layoutId` no necesita `AnimatePresence`: eso hace falta para animaciones de
 * SALIDA, y aquí no hay ninguna. Basta con que los dos extremos —la lámina del
 * acceso y la barra lateral— compartan un `LayoutGroup`, que es lo que le dice a
 * motion que son el mismo elemento aunque vivan en ramas distintas del árbol.
 */
function Rutas() {
  return (
    <LayoutGroup>
      <Routes>
        <Route path="/login" element={<Login />} />
        {/* ⚠ FUERA de `Protegida` y del `AppShell`, y las dos cosas a propósito:
                se llega con un token de sólo lectura y sin sesión, y sin armazón no
                hay ningún camino desde ahí a otra pantalla. */}
        <Route path="/t/:token" element={<TareaPublica />} />
        {/* Igual que la de tareas: la orden o el pago del enlace del bot, sin sesión
            y sin ningún camino a otra pantalla. */}
        <Route path="/p/:token" element={<OrdenPublica />} />
        <Route path="/i/:token" element={<IngresoPublica />} />
        <Route path="/r/:token" element={<ReportePublico />} />
        <Route path="/s/:token" element={<SemanaPublica />} />
        <Route path="/d/:token" element={<DisponibilidadPublica />} />
        <Route path="/e/:token" element={<ExpedientesPublica />} />
        <Route path="/g/:token" element={<GastosPublica />} />
        <Route path="/m/:token" element={<MacroPublica />} />
        <Route
          element={
            <Protegida>
              <AppShell />
            </Protegida>
          }
        >
          <Route path="/" element={<Entrada />} />
          <Route path="/expedientes" element={<Expedientes />} />
          <Route path="/expedientes/:id" element={<ExpedienteDetalle />} />
          <Route path="/cotizaciones" element={<Cotizaciones />} />
          <Route path="/cotizaciones/:id" element={<CotizacionDetalle />} />
          <Route path="/clientes" element={<Clientes />} />
          {/* ⚠ **Administración es un DEPARTAMENTO con sus vistas en pestañas**, y la
              vista va en la URL. La nómina financiera trae además su propia fila
              debajo, y por eso hay un segundo parámetro. Ver `Administracion.tsx`. */}
          <Route path="/administracion" element={<Administracion />} />
          {/* Las categorías dejaron de ser pestaña propia: se miran junto a los
              gastos que agrupan. Ruta explícita porque, sin ella, una vista
              desconocida cae a la primera pestaña sin decir nada. */}
          <Route
            path="/administracion/categorias-gasto"
            element={<Navigate to="/administracion/gastos" replace={true} />}
          />
          <Route path="/administracion/:vista" element={<Administracion />} />
          <Route path="/administracion/:vista/:sub" element={<Administracion />} />
          {/* Las direcciones de cuando «Sueldos y salarios» era una entrada suelta.
              `/nomina/contratos` se va a Talento humano, que es donde vive ahora lo
              laboral; las otras dos, a su pestaña de Administración. */}
          <Route path="/nomina" element={<Navigate to="/administracion/nomina" replace={true} />} />
          <Route path="/nomina/:vista" element={<RutaNominaVieja />} />
          {/* Talento humano, el otro departamento: fichas y contratos. */}
          <Route path="/talento-humano" element={<TalentoHumano />} />
          <Route path="/talento-humano/personas/:id" element={<ExpedienteLaboral />} />
          <Route path="/talento-humano/:vista" element={<TalentoHumano />} />
          <Route path="/talento-humano/documento/:id" element={<PaginaDocumentoTH modo="th" />} />
          <Route path="/talento-humano/evaluacion/:id" element={<Evaluacion />} />
          <Route path="/mis-evaluaciones" element={<MisEvaluaciones />} />
          <Route path="/mis-evaluaciones/:id" element={<MisEvaluaciones />} />
          <Route path="/guia" element={<Guia />} />
          <Route path="/plan-cuentas" element={<PlanCuentas />} />
          {/* Solvencias y presentaciones ante los entes (CUMP-1). */}
          <Route path="/cumplimiento" element={<Cumplimiento />} />
          <Route path="/cumplimiento/:vista" element={<Cumplimiento />} />
          {/* Lo que el empleado pide a Talento Humano, con SUS datos. */}
          <Route path="/mis-documentos" element={<MisSolicitudes />} />
          <Route path="/mis-documentos/:id" element={<PaginaDocumentoTH modo="autoservicio" />} />
          {/* El informe de gastos vive en Administración → «Reportes» (28/10). */}
          <Route
            path="/informes/gastos"
            element={<Navigate to="/administracion/reportes" replace={true} />}
          />
          {/* ⚠ **«Comercial» es una SECCIÓN del menú, no una pantalla**: cada módulo
              tiene la suya. `/comercial/<vista>` y `/catalogo/<vista>` son las de
              cuando fue un departamento con pestañas, y conservan la vista. */}
          <Route path="/tipos-proveedor" element={<TiposProveedor />} />
          <Route path="/comercial" element={<RutaComercialVieja />} />
          <Route path="/comercial/:vista" element={<RutaComercialVieja />} />
          <Route path="/catalogo" element={<RutaComercialVieja />} />
          <Route path="/catalogo/:vista" element={<RutaComercialVieja />} />
          <Route path="/proveedores" element={<Proveedores />} />
          <Route path="/proveedores/operativos" element={<Proveedores deAmbito="operativos" />} />
          <Route
            path="/proveedores/administrativos"
            element={<Proveedores deAmbito="administrativos" />}
          />
          {/* ⚠ **Las tres vistas del gasto se fueron del Catálogo a Administración**, y
              estas direcciones tienen que seguir llevando a su sitio: sin ellas,
              `/catalogo/gastos` abriría «Proveedores» —la primera pestaña— sin decir
              nada, que es peor que un 404. */}
          <Route
            path="/catalogo/gastos"
            element={<Navigate to="/administracion/gastos" replace={true} />}
          />
          <Route
            path="/catalogo/categorias-gasto"
            element={<Navigate to="/administracion/gastos" replace={true} />}
          />
          <Route
            path="/catalogo/programados"
            element={<Navigate to="/administracion/programados" replace={true} />}
          />
          <Route path="/tareas" element={<Tareas />} />
          <Route path="/buzon" element={<Buzon />} />
          <Route path="/avisos" element={<Avisos />} />
          <Route path="/perfil" element={<Perfil />} />
          <Route path="/mi-ficha" element={<MiFicha />} />
          {/* «Personas» dejó de ser entrada suelta: es la primera pestaña de su
              departamento. */}
          <Route
            path="/personas"
            element={<Navigate to="/talento-humano/personas" replace={true} />}
          />
          <Route path="/drive" element={<Drive />} />
          {/* Planificación estratégica es UNA entrada con cinco vistas en pestañas,
                  y la vista va en la URL. Ver `Planificacion.tsx`. */}
          <Route path="/planificacion" element={<Planificacion />} />
          <Route path="/planificacion/:vista" element={<Planificacion />} />
          <Route path="/planificacion/biblioteca/:id" element={<DocumentoSGI />} />
          {/* Las direcciones de cuando cada vista era su propia entrada: hay enlaces
                  guardados y marcadores, y tienen que seguir llevando a su pestaña. */}
          <Route path="/estrategia" element={<RutaVieja vista="estrategia" />} />
          <Route path="/organigrama" element={<RutaVieja vista="organigrama" />} />
          <Route path="/procesos" element={<RutaVieja vista="procesos" />} />
          <Route path="/biblioteca" element={<RutaVieja vista="biblioteca" />} />
          <Route path="/biblioteca/:id" element={<DocumentoViejo />} />
          <Route path="/manual" element={<RutaVieja vista="manual" />} />
          <Route path="/empresa" element={<Empresa />} />
          <Route path="/reportes" element={<Reportes />} />
          <Route path="/identidad" element={<ManualIdentidad />} />
          <Route path="/rotulacion" element={<Rotulacion />} />
          {/* El carnet vive en la ficha del empleado: la dirección vieja lleva a
                  «Personas», que es donde está. */}
          <Route
            path="/carnets"
            element={<Navigate to="/talento-humano/personas" replace={true} />}
          />
          <Route path="/plantillas" element={<Plantillas />} />
          <Route path="/credenciales" element={<Credenciales />} />
          {/* Dirección vieja: el catálogo de gastos vive en Administración. */}
          <Route path="/gastos" element={<Navigate to="/administracion/gastos" replace={true} />} />
          {/* La orden de pago —borrador, aprobación y ejecución— es la primera pestaña
              de Administración. Esta dirección es la de cuando tenía entrada propia. */}
          <Route
            path="/ordenes-pago"
            element={<Navigate to="/administracion/ordenes-pago" replace={true} />}
          />
          <Route path="/libro-compras" element={<LibroCompras />} />
          <Route path="/retenciones-provisionales" element={<RetencionesProvisionales />} />
          <Route path="/resumen-macro" element={<ResumenMacro />} />
          {/* El depósito: lo comprado entra al ejecutar el pago de su orden. */}
          {/* El depósito es ahora una pestaña del departamento; la dirección
              vieja sigue abriendo lo suyo. Ver `CadenaSuministros`. */}
          <Route path="/inventario" element={<RutaInventarioVieja />} />
          <Route path="/cadena-suministros" element={<CadenaSuministros />} />
          <Route path="/cadena-suministros/:vista" element={<CadenaSuministros />} />
          {/* El tablero de lo que falta por completar, de todo el sistema. */}
          <Route path="/pendientes" element={<Pendientes />} />
          {/* Las tres pantallas de dinero. Sin estas rutas compilan, pasan
                  el build y son inalcanzables en la aplicación. */}
          {/* Tesorería es UNA entrada con sus vistas en pestañas, y la vista va en
                  la URL. `/tesoreria` a secas abre las tasas; las direcciones viejas
                  —`/tesoreria/cuentas`— llevan a su pestaña. Ver `Tesoreria.tsx`. */}
          <Route path="/tesoreria" element={<Tesoreria />} />
          <Route path="/tesoreria/:vista" element={<Tesoreria />} />
          {/* El Libro de Cuentas es UNA entrada con por pagar y por cobrar en
                  pestañas, igual que Tesorería, y la vista va en la URL. Las
                  direcciones viejas —`/cxp` y `/cxc`— llevan a su pestaña. Ver
                  `LibroCuentas.tsx`. */}
          <Route path="/libro-cuentas" element={<LibroCuentas />} />
          <Route path="/libro-cuentas/:vista" element={<LibroCuentas />} />
          <Route path="/cxc" element={<Navigate to="/libro-cuentas/por-cobrar" replace={true} />} />
          <Route path="/cxp" element={<Navigate to="/libro-cuentas/por-pagar" replace={true} />} />
          <Route path="/portal" element={<MisDespachos />} />
          {/* Antes que `/portal/:id` y a propósito. React Router puntúa el
                  segmento estático por encima del dinámico, así que funcionaría
                  en cualquier orden — pero leído de arriba abajo, ponerlo después
                  parece un despacho llamado «buzon». */}
          <Route path="/portal/estado-cuenta" element={<MiEstadoCuenta />} />
          <Route path="/portal/buzon" element={<BuzonPortal />} />
          <Route path="/portal/:id" element={<DespachoDetalle />} />
          <Route path="/sistema-de-diseno" element={<SistemaDeDiseno />} />
          <Route path="/mapa" element={<MapaSistema />} />
          <Route path="/fletes" element={<Fletes />} />
          <Route path="/admin/usuarios" element={<Usuarios />} />
          <Route path="/admin/auditoria" element={<Auditoria />} />
          <Route path="*" element={<Marcador titulo="Ruta no encontrada" />} />
        </Route>
      </Routes>
    </LayoutGroup>
  )
}

/** Las rutas del portal de transporte: el acceso y sus cinco pantallas, nada del ERP. */
function RutasTransporte() {
  return (
    <LayoutGroup>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          element={
            <Protegida>
              <AppShell />
            </Protegida>
          }
        >
          <Route path="/cargas" element={<PaginaCargas />} />
          <Route path="/recibos" element={<PaginaRecibos />} />
          <Route path="/empresas" element={<PaginaCatalogo clase="EMPRESA" />} />
          <Route path="/vehiculos" element={<PaginaCatalogo clase="VEHICULO" />} />
          <Route path="/choferes" element={<PaginaCatalogo clase="CHOFER" />} />
          <Route path="*" element={<Navigate to="/cargas" replace={true} />} />
        </Route>
      </Routes>
    </LayoutGroup>
  )
}

// ⚠ **Antes de montar.** Una excepción durante el arranque también tiene que
// quedar registrada, y si esto corriera dentro de un efecto se perdería justo
// la clase de fallo que deja la aplicación sin arrancar.
engancharBitacora()

// ⚠ **El portal de transporte es el MISMO sistema con otras rutas** (25/10): en
// `transporte.<dominio>` se monta el acceso, la fotografía y el armazón de siempre
// —«como herramienta del 3.0 debe conservar la misma apariencia», pedido del usuario—, pero
// con `RutasTransporte` y no con las del ERP. El cerrojo real es del servidor
// (`deps.portal_admite`): en ese host sólo entra una cuenta TRANSPORTISTA.

if (ES_PORTAL_TRANSPORTE) document.title = NOMBRE_PORTAL_TRANSPORTE

createRoot(document.getElementById("raiz") as HTMLElement).render(
  <StrictMode>
    <QueryClientProvider client={clienteQuery}>
      <BrowserRouter>
        {/* ⚠ **`reducedMotion="user"` para TODO el árbol (12/10/2026).** El reset de
            CSS de `globales.css` no detiene a motion, y cada componente tenía que
            acordarse de `useReducedMotion`: el armazón, el viaje de la lámina y la
            entrada del acceso no se acordaban. Con esto, quien pidió menos
            movimiento no ve desplazamientos ni viajes —quedan los fundidos—. */}
        <MotionConfig reducedMotion="user">
          <ArranqueSesion>
            <Escenario>{ES_PORTAL_TRANSPORTE ? <RutasTransporte /> : <Rutas />}</Escenario>
          </ArranqueSesion>
          <PilaAvisos />
        </MotionConfig>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
)

// La aplicación instalable: ver `public/sw.js`, que dice por qué no guarda nada
// en caché. Sólo en producción: en desarrollo, un service worker vivo se queda
// con las navegaciones y confunde la recarga en caliente de Vite.
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      // Sin él la aplicación funciona igual; sólo deja de ofrecerse para instalar.
    })
  })
}
